import ArrowForward from '@kmx/mui-icons/KmxArrowRightFilled';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import type { TypographyVariant } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { useCallback, useMemo } from 'react';
import type { PromoCardProps } from '.';
import { clickTracking } from '../../utils/analytics';
import { InstantOfferImage } from '../../utils/stockImage';

const NoOfferCard = ({
    baseUrl = '',
    breakpoint,
    originPage = '',
    position = '',
    redirectPath = '/sell-my-car',
}: PromoCardProps) => {
    const isSmallScreen = useMemo(() => breakpoint == 'xs' || breakpoint == 'sm', [breakpoint]);
    const isExtraLargeScreen = useMemo(() => breakpoint == 'xl', [breakpoint]);

    const bodyTextProps: {
        // Just the color values we're using
        color: 'textSecondary' | 'textPrimary';
        variant: TypographyVariant;
    } = useMemo(() => {
        if (isExtraLargeScreen) {
            return { color: 'textPrimary', variant: 'h5' };
        } else if (isSmallScreen) {
            return { color: 'textSecondary', variant: 'caption' };
        }
        // MD->LG
        return { color: 'textSecondary', variant: 'body2' };
    }, [isSmallScreen, isExtraLargeScreen]);

    const handleOnClick = useCallback(() => {
        clickTracking('get my offer', position);
        let providedBaseUrl = baseUrl;
        if (!baseUrl && window?.location?.origin) {
            providedBaseUrl = window.location.origin;
        }
        const url = new URL(redirectPath, providedBaseUrl);

        url.hash = 'hero-label';

        if (originPage) {
            url.searchParams.append('originPage', originPage);
        }

        if (window) {
            window.open(url, '_self');
        }
    }, [baseUrl, originPage, position, redirectPath]);

    return (
        <Paper
            onClick={handleOnClick}
            sx={{
                borderRadius: isSmallScreen ? '24px' : '32px',
                height: !isSmallScreen && !isExtraLargeScreen ? '204px' : undefined,
                padding: !isSmallScreen && !isExtraLargeScreen ? '12px' : 1,
                paddingRight: isExtraLargeScreen ? '40px' : isSmallScreen ? 1 : '12px',
            }}
            variant="outlined"
        >
            <Grid container spacing={isSmallScreen ? '12px' : '24px'} alignItems={'center'}>
                <Grid size={'auto'}>
                    <Box
                        sx={{
                            display: 'flex',
                            height: '100%',
                            width: isExtraLargeScreen ? '104px' : isSmallScreen ? '72px' : '180px',
                        }}
                    >
                        <img
                            alt="Instant offer"
                            src={InstantOfferImage}
                            height={'auto'}
                            width={'100%'}
                            style={{ borderRadius: isSmallScreen ? '16px' : '24px', maxWidth: '100%' }}
                        />
                    </Box>
                </Grid>
                <Grid size={'grow'}>
                    <Stack gap={2}>
                        <Stack>
                            <Typography component={'h2'} variant={isSmallScreen ? 'subtitle1' : 'h5'}>
                                Wanna sell your car?
                            </Typography>
                            <Typography component={'p'} {...bodyTextProps}>
                                Get a real offer in 2 minutes
                            </Typography>
                        </Stack>
                        {!isSmallScreen && !isExtraLargeScreen && (
                            <Button
                                endIcon={<ArrowForward color="primary" />}
                                sx={{
                                    width: 'max-content',
                                }}
                                variant="outlined"
                            >
                                Get my offer
                            </Button>
                        )}
                    </Stack>
                </Grid>
                <Grid
                    container
                    justifyContent={'center'}
                    size={'auto'}
                    sx={{ display: isSmallScreen || isExtraLargeScreen ? 'flex' : 'none' }}
                >
                    {isExtraLargeScreen ? (
                        <Button endIcon={<ArrowForward color="primary" />} variant="outlined">
                            Get my offer
                        </Button>
                    ) : (
                        <ArrowForward color="primary" />
                    )}
                </Grid>
            </Grid>
        </Paper>
    );
};

export default NoOfferCard;
